import { useState, useEffect } from 'react';
import { ArrowLeft, Star, MessageSquare, TrendingUp, RefreshCw, User } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  getAdminAgentFeedback,
  getAdminFeedbackStats,
} from '../../services/feedbackService';
import type { AgentFeedbackWithUser, FeedbackStats } from '../../services/feedbackService';
import { logger } from '../../lib/logger';

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`w-4 h-4 ${
            star <= rating
              ? 'text-yellow-400 fill-yellow-400'
              : 'text-slate-600'
          }`}
        />
      ))}
    </div>
  );
}

export function AdminFeedback() {
  const [feedback, setFeedback] = useState<AgentFeedbackWithUser[]>([]);
  const [stats, setStats] = useState<FeedbackStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const loadData = async (showRefresh = false) => {
    if (showRefresh) setIsRefreshing(true);
    try {
      const [feedbackData, statsData] = await Promise.all([
        getAdminAgentFeedback(100, 0),
        getAdminFeedbackStats(),
      ]);
      setFeedback(feedbackData);
      setStats(statsData);
    } catch (error) {
      logger.error('Error loading feedback:', error);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link
            to="/admin"
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-xl transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white">Nutzer-Feedback</h1>
            <p className="text-slate-400 mt-1">Bewertungen und Feedback zur MAMAL-Plattform</p>
          </div>
        </div>
        <button
          onClick={() => loadData(true)}
          disabled={isRefreshing}
          className="flex items-center gap-2 px-4 py-2.5 bg-slate-800 text-slate-300 rounded-xl hover:bg-slate-700 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          Aktualisieren
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-violet-500/20 rounded-xl flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-violet-400" />
            </div>
          </div>
          <p className="text-3xl font-bold text-white">{stats?.total_feedback || 0}</p>
          <p className="text-sm text-slate-400 mt-1">Gesamt Feedback</p>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-yellow-500/20 rounded-xl flex items-center justify-center">
              <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
            </div>
          </div>
          <p className="text-3xl font-bold text-white">{stats?.average_rating || '–'}</p>
          <p className="text-sm text-slate-400 mt-1">Durchschnitt</p>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-emerald-400" />
            </div>
            <span className="text-xs font-medium text-emerald-400 bg-emerald-400/10 px-2 py-1 rounded-full">
              30 Tage
            </span>
          </div>
          <p className="text-3xl font-bold text-white">{stats?.recent_count || 0}</p>
          <p className="text-sm text-slate-400 mt-1">Neues Feedback</p>
        </div>

        {/* Rating Distribution */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
          <p className="text-sm text-slate-400 mb-3">Verteilung</p>
          <div className="space-y-1.5">
            {[5, 4, 3, 2, 1].map((rating) => {
              const count = stats?.rating_distribution?.[rating.toString()] || 0;
              const total = stats?.total_feedback || 1;
              const pct = Math.round((count / total) * 100) || 0;
              return (
                <div key={rating} className="flex items-center gap-2">
                  <span className="text-xs text-slate-400 w-3">{rating}</span>
                  <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                  <div className="flex-1 h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-yellow-400 rounded-full transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="text-xs text-slate-500 w-8">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Feedback List */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
        <div className="p-6 border-b border-slate-800">
          <h2 className="text-lg font-semibold text-white">Alle Bewertungen</h2>
        </div>

        {feedback.length === 0 ? (
          <div className="p-12 text-center">
            <MessageSquare className="w-12 h-12 text-slate-700 mx-auto mb-3" />
            <p className="text-slate-400">Noch kein Feedback vorhanden.</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-800">
            {feedback.map((item) => (
              <div key={item.id} className="p-5 hover:bg-slate-800/50 transition-colors">
                <div className="flex items-start gap-4">
                  {/* User Avatar */}
                  <div className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-slate-500" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-4 mb-2">
                      <div>
                        <p className="font-medium text-white">
                          {item.user_name || 'Unbekannt'}
                        </p>
                        <p className="text-sm text-slate-500">{item.user_email}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <StarRating rating={item.rating} />
                        <p className="text-xs text-slate-500 mt-1">
                          {formatDate(item.created_at)}
                        </p>
                      </div>
                    </div>

                    {item.comment && (
                      <p className="text-slate-300 text-sm bg-slate-800/50 rounded-lg p-3 mt-2">
                        "{item.comment}"
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
